import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config';
const TOKEN_KEY = 'naijanest_token';
const USER_KEY = 'naijanest_user';
const DEVICE_TRUST_KEY = 'naijanest_device_trust';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [user]);

  const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

  const clearError = useCallback(() => setError(null), []);

  const register = useCallback(async ({ name, email, password, role, referralCode }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, role, referralCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Registration failed.');
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    setError(null);
    try {
      const deviceTrustToken = localStorage.getItem(DEVICE_TRUST_KEY) || undefined;
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, deviceTrustToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'account_deactivated') {
          setError(data.error);
          return {
            ok: false,
            code: 'account_deactivated',
            reactivationToken: data.reactivationToken || null,
            error: data.error,
          };
        }
        throw new Error(data.error || 'Login failed.');
      }

      if (data.requiresTwoFactor) {
        return {
          ok: true,
          requiresTwoFactor: true,
          twoFactorToken: data.twoFactorToken,
          user: data.user || null,
        };
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, requiresTwoFactor: false };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyTwoFactorLogin = useCallback(async ({ code, twoFactorToken, rememberDevice }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/totp-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code, twoFactorToken, rememberDevice }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || '2FA verification failed.');
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      if (data.deviceTrustToken) {
        localStorage.setItem(DEVICE_TRUST_KEY, data.deviceTrustToken);
      }
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to request a password reset.');
      }
      return { ok: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async ({ token, newPassword }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset password.');
      }
      return { ok: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmEmail = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/confirm-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to confirm email.');
      }
      updateCurrentUser({ emailConfirmed: true });
      return { ok: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const resendConfirmation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`${API_BASE}/auth/resend-confirmation`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to resend confirmation email.');
      }
      return { ok: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Used by Google Sign-In (and any OAuth flow) to sync token + user into context
  const loginWithToken = useCallback(({ token, user: u }) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
  }, []);

  // Exchanges a Google ID token credential (from the sign-in button or the
  // One Tap prompt) for a session. Shared by both so the credential-exchange
  // logic lives in one place.
  const googleSignIn = useCallback(async (credential) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Google sign-in failed.');
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCurrentUser = useCallback((patch) => {
    setUser((previousUser) => {
      if (!previousUser) {
        return previousUser;
      }
      return {
        ...previousUser,
        ...(patch || {}),
      };
    });
  }, []);

  /**
   * Returns fetch options with the auth Bearer header included.
   * Usage: fetch(url, authFetch({ method: "POST", body: ... }))
   */
  const authFetch = useCallback(
    (options = {}) => {
      const token = getToken();
      return {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };
    },
    [getToken],
  );

  /**
   * Same as fetch(url, authFetch(options)), but also treats a 401 response
   * as proof the session died server-side (expired, revoked from another
   * device, deactivated, etc.) and logs out immediately instead of leaving
   * the app showing a stale "signed in" state that every subsequent
   * request will just keep failing against. A banned/suspended account now
   * gets an accurate 403 { code: "account_suspended" } straight from
   * requireAuth (rather than only the generic 401 from its session getting
   * revoked), and a mid-session deactivation returns 403
   * { code: "account_deactivated" } - both are read from a cloned response
   * (so the original body is still readable by the caller) and treated the
   * same as a 401 here.
   * Usage: authedFetch(url, { method: "POST", body: ... })
   */
  const authedFetch = useCallback(
    async (url, options = {}) => {
      const response = await fetch(url, authFetch(options));
      if (response.status === 401) {
        logout();
      } else if (response.status === 403) {
        try {
          const data = await response.clone().json();
          if (data?.code === 'account_suspended' || data?.code === 'account_deactivated') {
            logout();
          }
        } catch {
          // Not a JSON body - nothing to act on.
        }
      }
      return response;
    },
    [authFetch, logout],
  );

  useEffect(() => {
    const token = getToken();
    if (!token) {
      return;
    }

    let cancelled = false;

    const syncCurrentUser = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to fetch current user.');
        }

        if (!cancelled) {
          setUser(data.user || null);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
        }
      }
    };

    syncCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const reactivateAccount = useCallback(async (reactivationToken) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/users/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${reactivationToken}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reactivate account.');
      }
      return { ok: true };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const startTwoFactorSetup = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res = await authedFetch(`${API_BASE}/auth/2fa/setup`, { method: 'POST' });
      if (res.status === 404) {
        res = await authedFetch(`${API_BASE}/auth/totp-setup`, { method: 'POST' });
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to set up 2FA.');
      }
      return { ok: true, ...data };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const verifyTwoFactorSetup = useCallback(
    async ({ code }) => {
      setLoading(true);
      setError(null);
      try {
        const verifyPayload = {
          token: code,
        };

        let res = await authedFetch(`${API_BASE}/auth/2fa/verify`, {
          method: 'POST',
          body: JSON.stringify(verifyPayload),
        });
        if (res.status === 404) {
          res = await authedFetch(`${API_BASE}/auth/totp-verify`, {
            method: 'POST',
            body: JSON.stringify(verifyPayload),
          });
        }
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || '2FA setup verification failed.');
        }

        if (data.user) {
          setUser(data.user);
        }

        return { ok: true, ...data };
      } catch (err) {
        setError(err.message);
        return { ok: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [authedFetch],
  );

  const disableTwoFactorSetup = useCallback(
    async ({ currentPassword }) => {
      setLoading(true);
      setError(null);
      try {
        const disablePayload = { currentPassword };
        let res = await authedFetch(`${API_BASE}/auth/2fa/disable`, {
          method: 'POST',
          body: JSON.stringify(disablePayload),
        });
        if (res.status === 404) {
          res = await authedFetch(`${API_BASE}/auth/totp-disable`, {
            method: 'POST',
            body: JSON.stringify(disablePayload),
          });
        }
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to disable 2FA.');
        }

        if (data.user) {
          setUser(data.user);
        }

        return { ok: true, ...data };
      } catch (err) {
        setError(err.message);
        return { ok: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [authedFetch],
  );

  const regenerateTwoFactorBackupCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res = await authedFetch(`${API_BASE}/auth/2fa/backup-codes/regenerate`, {
        method: 'POST',
      });
      if (res.status === 404) {
        res = await authedFetch(`${API_BASE}/auth/totp-backup-codes/regenerate`, {
          method: 'POST',
        });
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to regenerate backup codes.');
      }
      return { ok: true, ...data };
    } catch (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const hasRole = useCallback(
    (...roles) => {
      const currentRole = String(user?.role || '')
        .trim()
        .toLowerCase();
      if (!currentRole) {
        return false;
      }

      const normalizedRoles = roles
        .map((role) =>
          String(role || '')
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean);

      if (normalizedRoles.includes(currentRole)) {
        return true;
      }

      // Admins supersede every other role - anywhere a page or feature is
      // gated on being a buyer, seller, agent, or contributor, an admin
      // should pass too, rather than being redirected away or seeing a
      // blocked/empty state.
      if ((currentRole === 'admin' || currentRole === 'superadmin') && normalizedRoles.length > 0) {
        return true;
      }

      return false;
    },
    [user],
  );

  const isAdmin = hasRole('admin', 'superadmin');
  const canListProperty = !!user;

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      register,
      login,
      verifyTwoFactorLogin,
      reactivateAccount,
      forgotPassword,
      resetPassword,
      confirmEmail,
      resendConfirmation,
      startTwoFactorSetup,
      verifyTwoFactorSetup,
      disableTwoFactorSetup,
      regenerateTwoFactorBackupCodes,
      loginWithToken,
      googleSignIn,
      updateCurrentUser,
      logout,
      getToken,
      authFetch,
      authedFetch,
      hasRole,
      isAdmin,
      canListProperty,
      clearError,
    }),
    [
      user,
      loading,
      error,
      register,
      login,
      verifyTwoFactorLogin,
      reactivateAccount,
      forgotPassword,
      resetPassword,
      confirmEmail,
      resendConfirmation,
      startTwoFactorSetup,
      verifyTwoFactorSetup,
      disableTwoFactorSetup,
      regenerateTwoFactorBackupCodes,
      loginWithToken,
      googleSignIn,
      updateCurrentUser,
      logout,
      getToken,
      authFetch,
      authedFetch,
      hasRole,
      isAdmin,
      canListProperty,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
